import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { UserContext } from "../../context/user-context";

import getAccountOrders, { getPastMonthDateRange } from '../../functions/getAccountOrders';

const useStyles = makeStyles({
  root: {
    '& .MuiSelect-outlined.MuiSelect-outlined': {
      height:12,
    }
  },
  formtable:{
    marginBottom: 20,
    color: '#447095',
    maxHeight: '50vh',
    overflowY: 'auto',
    backgroundColor: '#F3F8F5',
    border: '1px solid #0C4C35',
    borderRadius: 0,
  },
  ordersTable: {
    minWidth: 900,
    '& .MuiTableHead-root .MuiTableCell-root': {
      backgroundColor: '#0C4C35',
      color: '#FFF',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      borderBottom: '1px solid #0C4C35',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
      color: '#000',
      fontSize: 12,
      verticalAlign: 'top',
      borderBottom: '1px solid #D1DED5',
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
      backgroundColor: '#F3F8F5',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: '#E4F2E9',
    },
  },
  totalRow: {
    '& .MuiTableCell-root': {
      fontWeight: 700,
      position: 'sticky',
      bottom: 0,
      zIndex: 2,
      color: '#FFF',
      backgroundColor: '#0C4C35',
      borderTop: '2px solid #63D764',
    },
  },
  monthSelector: {
    minWidth: 140,
  },
});

function formatOrderProducts(products, orderType) {
  const entries = Array.isArray(products)
    ? products
    : typeof products === 'string'
      ? products.replace(/^\{|\}$/g, '').split(',')
      : [];

  return entries
    .map((entry) => String(entry).trim().replace(/^"|"$/g, ''))
    .filter(Boolean)
    .map((entry) => {
      const separator = entry.indexOf('-');
      const label = (separator < 0 ? entry : entry.slice(0, separator)).trim();
      const format = separator < 0 ? '' : entry.slice(separator + 1).trim();

      return orderType !== 'print' && format ? `${label} (${format})` : label;
    })
    .join(', ');
}

export default function OrderDialog() {

  const classes = useStyles();
  const [uState] = useContext(UserContext);
  const [orderRows, setOrderRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthOffset, setMonthOffset] = useState(0);

  const monthOptions = Array.from({ length: 12 }, (_, offset) => {
    const { fromDate } = getPastMonthDateRange(offset);
    return {
      offset,
      label: new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit' }).format(fromDate),
    };
  });

  useEffect(() => {
    let active = true;

    async function loadOrders() {
      setLoading(true);
      const orders = await getAccountOrders(uState.companyid, monthOffset);
      if (active) {
        setOrderRows(Array.isArray(orders) ? orders : []);
        setLoading(false);
      }
    }

    loadOrders();
    return () => {
      active = false;
    };
  }, [uState.companyid, monthOffset]);

  const totals = orderRows.reduce((sum, order) => ({
    price: sum.price + (Number.parseFloat(order.price) || 0),
    vat: sum.vat + (Number.parseFloat(order.vat) || 0),
    total: sum.total + (Number.parseFloat(order.total) || 0),
  }), { price: 0, vat: 0, total: 0 });

  const formatCurrency = (amount) => `£${amount.toFixed(2)}`;

  return (
    <div className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box component="h2" fontWeight="fontWeightBold" fontSize={18} m={0}>
          Order History
        </Box>
        <FormControl variant="outlined" size="small" className={classes.monthSelector}>
          <InputLabel id="order-month-label">Month</InputLabel>
          <Select
            labelId="order-month-label"
            id="order-month"
            value={monthOffset}
            onChange={(event) => setMonthOffset(Number(event.target.value))}
            label="Month"
          >
            {monthOptions.map((month) => (
              <MenuItem key={month.offset} value={month.offset}>{month.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {loading ? (
        <Box fontWeight="fontWeightRegular" fontSize={18}>Loading orders...</Box>
      ) : orderRows.length === 0 ? (
        <Box fontWeight="fontWeightRegular" fontSize={18}>No orders found.</Box>
      ) : (
        <TableContainer component={Paper} className={classes.formtable}>
          <Table size="small" stickyHeader className={classes.ordersTable} aria-label="Account orders">
            <TableHead>
              <TableRow>
                <TableCell>Order date</TableCell>
                <TableCell>Order #</TableCell>
                <TableCell>Ordered by</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>PO number</TableCell>
                <TableCell>Products</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="right">VAT</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderRows.map((order) => (
                <TableRow key={order.orderid}>
                  <TableCell>{order.orderdate || '-'}</TableCell>
                  <TableCell>{order.orderid || '-'}</TableCell>
                  <TableCell>{order.username || '-'}</TableCell>
                  <TableCell>{order.projectname || '-'}</TableCell>
                  <TableCell>{order.userref || '-'}</TableCell>
                  <TableCell>{formatOrderProducts(order.products, order.ordertype) || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(Number.parseFloat(order.price) || 0)}</TableCell>
                  <TableCell align="right">{formatCurrency(Number.parseFloat(order.vat) || 0)}</TableCell>
                  <TableCell align="right">{formatCurrency(Number.parseFloat(order.total) || 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className={classes.totalRow}>
                <TableCell colSpan={6} align="right">Total</TableCell>
                <TableCell align="right">{formatCurrency(totals.price)}</TableCell>
                <TableCell align="right">{formatCurrency(totals.vat)}</TableCell>
                <TableCell align="right">{formatCurrency(totals.total)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </div>
  );
}